import ExcelJS from 'exceljs';
import { resolveProductUnit } from './unitSelectUtils';

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFCFFAFE' },
};

export const MAX_IMPORT_ROWS = 2000;
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const PO_COL = {
  sku: 'SKU o ID producto',
  qty: 'Cantidad',
  unit: 'Unidad (código)',
  price: 'Precio unitario',
  project: 'Proyecto (código)',
  igv: 'Afecto IGV (si/no)',
};

const DOC_COL = {
  tipo: 'Tipo (producto o servicio)',
  sku: 'SKU o ID',
  qty: 'Cantidad',
  unit: 'Unidad (código)',
  price: 'Precio unitario',
  project: 'Proyecto (código)',
  igv: 'Afecto IGV (si/no)',
  desc: 'Descripción (opcional)',
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function cellText(cell) {
  if (cell == null) return '';
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'object' && v.text != null) return String(v.text).trim();
  return String(v).trim();
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet, requiredCols) {
  const max = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= max; r++) {
    const row = sheet.getRow(r);
    const m = headerMapFromRow(row);
    if (requiredCols.every((c) => m[c])) {
      return { rowIndex: r, colMap: m };
    }
  }
  return null;
}

function findProduct(products, raw) {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t) return null;
  if (/^\d+$/.test(t)) {
    const id = parseInt(t, 10);
    const byId = products.find((p) => p.id === id);
    if (byId) return byId;
  }
  const low = t.toLowerCase();
  return (
    products.find(
      (p) =>
        (p.sku && String(p.sku).toLowerCase() === low) ||
        (p.code && String(p.code).toLowerCase() === low),
    ) || null
  );
}

function findService(services, raw) {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t) return null;
  if (/^\d+$/.test(t)) {
    const id = parseInt(t, 10);
    const byId = services.find((s) => s.id === id);
    if (byId) return byId;
  }
  const low = t.toLowerCase();
  return (
    services.find((s) => s.sku && String(s.sku).toLowerCase() === low) ||
    services.find((s) => s.name && String(s.name).toLowerCase() === low) ||
    null
  );
}

function resolveUnit(units, uRaw, product, rowNum, errors, productConversionsEnabled = false) {
  const resolved = resolveProductUnit({
    units,
    product,
    rawUnit: uRaw,
    productConversionsEnabled,
  });
  if (resolved.ok) {
    return {
      code: resolved.code,
      formula: resolved.formula || '',
      isCustom: resolved.isCustom,
    };
  }
  errors.push(`Fila ${rowNum}: ${resolved.error}`);
  return null;
}

const CONVERSION_IMPORT_NOTE =
  '• Si tiene activas las conversiones por producto, en «Unidad» puede usar códigos de tasas personalizadas (ej. BOLSA) configuradas en cada producto, además del catálogo general.';

function resolveProject(projects, pRaw, rowNum, errors) {
  if (!pRaw) return '';
  const pr = projects.find((p) => String(p.code || '').toLowerCase() === pRaw.toLowerCase());
  if (pr) return String(pr.id);
  errors.push(`Fila ${rowNum}: proyecto «${pRaw}» no encontrado. Deje vacío o use el código del proyecto.`);
  return null;
}

/** @returns {boolean | null} null = error de texto no reconocido */
function parseHasIgv(raw) {
  if (raw == null || String(raw).trim() === '') return true;
  const k = String(raw).trim().toLowerCase();
  if (['si', 'sí', 's', 'yes', 'y', '1', 'true', 'afecto', 'con igv', 'gravado'].includes(k)) return true;
  if (['no', 'n', '0', 'false', 'sin igv', 'exonerado', 'inafecto', 'no gravado'].includes(k)) return false;
  return null;
}

function parseTipo(raw) {
  const k = String(raw || 'producto').trim().toLowerCase();
  if (['producto', 'product', 'p', 'prod'].includes(k)) return 'product';
  if (['servicio', 'service', 's', 'serv'].includes(k)) return 'service';
  return null;
}

function parseQty(qtyVal, rowNum, errors) {
  const qtyNum = parseFloat(String(qtyVal).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
    errors.push(`Fila ${rowNum}: cantidad debe ser un número mayor que 0.`);
    return null;
  }
  return Math.round(qtyNum * 1000) / 1000;
}

function parsePrice(priceRaw, rowNum, colLabel, errors) {
  if (priceRaw == null || String(priceRaw).trim() === '') {
    errors.push(`Fila ${rowNum}: ${colLabel} es obligatorio y debe ser mayor que 0.`);
    return null;
  }
  const pu = parseFloat(String(priceRaw).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(pu) || pu <= 0) {
    errors.push(`Fila ${rowNum}: ${colLabel} debe ser un número mayor que 0.`);
    return null;
  }
  return Math.round(pu * 10000) / 10000;
}

function roundTo4(n) {
  return Math.round(parseFloat(n) * 10000) / 10000;
}

function buildDocumentTotals(quantity, unitPrice, hasIgv, igvRate) {
  const subtotal = roundTo4(quantity * unitPrice);
  const taxRate = hasIgv ? igvRate : 0;
  const taxAmount = roundTo4(subtotal * (taxRate / 100));
  const total = roundTo4(subtotal + taxAmount);
  return { subtotal, tax_rate: taxRate, tax_amount: taxAmount, total };
}

async function loadWorkbookSheet(file) {
  if (file && file.size > MAX_FILE_SIZE_BYTES) {
    return { error: 'El archivo supera el máximo de 5 MB.' };
  }
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const sheet = wb.getWorksheet('Items') || wb.worksheets[0];
  if (!sheet) return { error: 'No se encontró ninguna hoja en el archivo.' };
  return { sheet };
}

function addInstructionsSheet(wb, lines) {
  const inst = wb.addWorksheet('Instrucciones');
  lines
    .filter((line) => line != null)
    .forEach((line, i) => {
      const c = inst.getCell(1 + i, 1);
      c.value = line;
      const bold =
        line === 'LEYENDA Y USO DE LA PLANTILLA' ||
        line === 'COLUMNAS' ||
        line === 'VALIDACIÓN AL IMPORTAR';
      if (bold) c.font = { bold: true, size: 12 };
    });
  inst.getColumn(1).width = 100;
}

// ——— Orden de compra ———

export async function downloadPurchaseOrderItemsTemplate() {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Items', { views: [{ state: 'frozen', ySplit: 1 }] });
  const headers = [
    PO_COL.sku,
    PO_COL.qty,
    PO_COL.unit,
    PO_COL.price,
    PO_COL.project,
    PO_COL.igv,
  ];
  const hRow = ws.addRow(headers);
  hRow.font = { bold: true };
  headers.forEach((_, i) => {
    hRow.getCell(i + 1).fill = HEADER_FILL;
  });
  ws.addRow(['EJ-SKU-001', 1, 'kg', 10.5, 'PROJ-1', 'si']);
  ws.columns = headers.map((h) => ({ width: Math.min(28, Math.max(14, h.length + 2)) }));

  addInstructionsSheet(wb, [
    'LEYENDA Y USO DE LA PLANTILLA',
    'Orden de compra — ítems de productos',
    '',
    'COLUMNAS',
    `• ${PO_COL.sku}: código SKU o ID numérico del producto (obligatorio).`,
    `• ${PO_COL.qty}: cantidad mayor que 0 (obligatorio).`,
    `• ${PO_COL.unit}: código de unidad; vacío = unidad del producto.`,
    `• ${PO_COL.price}: precio unitario mayor que 0 (obligatorio).`,
    `• ${PO_COL.project}: código de proyecto; opcional.`,
    `• ${PO_COL.igv}: si / no (vacío = sí, afecto a IGV).`,
    '',
    'VALIDACIÓN AL IMPORTAR',
    '• Producto debe existir en el catálogo.',
    CONVERSION_IMPORT_NOTE,
    '• Máximo 2000 filas por archivo. Solo .xlsx.',
    '• Use la hoja «Items»; no cambie los nombres de la primera fila.',
  ]);

  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(buf, 'plantilla-items-orden-compra.xlsx');
}

/**
 * @returns {Promise<{ items: object[], errors: string[], warnings: string[] }>}
 */
export async function parsePurchaseOrderItemsExcel(file, { products, units, projects, productConversionsEnabled = false }) {
  const errors = [];
  const warnings = [];
  const items = [];
  const loaded = await loadWorkbookSheet(file);
  if (loaded.error) {
    errors.push(loaded.error);
    return { items, errors, warnings };
  }
  const { sheet } = loaded;
  const found = findHeaderRow(sheet, [PO_COL.sku, PO_COL.qty, PO_COL.price]);
  if (!found) {
    errors.push(
      `No se encontró la fila de encabezados. Debe incluir «${PO_COL.sku}», «${PO_COL.qty}» y «${PO_COL.price}».`,
    );
    return { items, errors, warnings };
  }
  const { colMap, rowIndex: headerRow } = found;
  const cSku = colMap[PO_COL.sku];
  const cQty = colMap[PO_COL.qty];
  const cUnit = colMap[PO_COL.unit];
  const cPrice = colMap[PO_COL.price];
  const cProj = colMap[PO_COL.project];
  const cIgv = colMap[PO_COL.igv];

  for (let r = headerRow + 1; r <= sheet.rowCount; r++) {
    const row = sheet.getRow(r);
    const skuVal = cSku ? cellText(row.getCell(cSku)) : '';
    const qtyVal = cQty ? cellText(row.getCell(cQty)) : '';
    if (!String(skuVal).trim() && !String(qtyVal).trim()) continue;
    if (items.length >= MAX_IMPORT_ROWS) {
      errors.push(`Solo se importan como máximo ${MAX_IMPORT_ROWS} filas.`);
      break;
    }
    if (!String(skuVal).trim()) {
      errors.push(`Fila ${r}: falta ${PO_COL.sku}.`);
      continue;
    }
    const product = findProduct(products, skuVal);
    if (!product) {
      errors.push(`Fila ${r}: no se encontró producto «${skuVal}».`);
      continue;
    }
    const qty = parseQty(qtyVal, r, errors);
    if (qty == null) continue;
    const priceRaw = cPrice ? cellText(row.getCell(cPrice)) : '';
    const unitPrice = parsePrice(priceRaw, r, PO_COL.price, errors);
    if (unitPrice == null) continue;

    const uRaw = cUnit ? String(cellText(row.getCell(cUnit)) || '').trim() : '';
    const uom = resolveUnit(units, uRaw, product, r, errors, productConversionsEnabled);
    if (uom === null) continue;

    const pRaw = cProj ? String(cellText(row.getCell(cProj)) || '').trim() : '';
    const projectId = resolveProject(projects, pRaw, r, errors);
    if (projectId === null) continue;

    const igvRaw = cIgv ? cellText(row.getCell(cIgv)) : '';
    const hasIgv = parseHasIgv(igvRaw);
    if (hasIgv === null) {
      errors.push(`Fila ${r}: ${PO_COL.igv} no reconocido. Use si o no.`);
      continue;
    }

    items.push({
      product_id: product.id,
      unit_of_measure: uom.code,
      project_id: projectId,
      quantity: qty,
      unit_price: unitPrice,
      has_igv: hasIgv,
      _label: product.name || skuVal,
      _unitFormula: uom.formula || '',
      _unitIsCustom: uom.isCustom,
    });
  }

  if (items.length === 0 && errors.length === 0) {
    errors.push('No se importó ninguna fila con datos.');
  }
  return { items, errors, warnings };
}

// ——— Documento ———

export async function downloadDocumentItemsTemplate() {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Items', { views: [{ state: 'frozen', ySplit: 1 }] });
  const headers = [
    DOC_COL.tipo,
    DOC_COL.sku,
    DOC_COL.qty,
    DOC_COL.unit,
    DOC_COL.price,
    DOC_COL.project,
    DOC_COL.igv,
    DOC_COL.desc,
  ];
  const hRow = ws.addRow(headers);
  hRow.font = { bold: true };
  headers.forEach((_, i) => {
    hRow.getCell(i + 1).fill = HEADER_FILL;
  });
  ws.addRow(['producto', 'EJ-SKU-001', 1, 'kg', 10.5, 'PROJ-1', 'si', '']);
  ws.addRow(['servicio', 'SRV-001', 1, '', 100, '', 'si', 'Descripción del servicio']);
  ws.columns = headers.map((h) => ({ width: Math.min(30, Math.max(14, h.length + 2)) }));

  addInstructionsSheet(wb, [
    'LEYENDA Y USO DE LA PLANTILLA',
    'Documento — ítems de productos y servicios',
    '',
    'COLUMNAS',
    `• ${DOC_COL.tipo}: producto o servicio (por defecto producto si está vacío).`,
    `• ${DOC_COL.sku}: SKU o ID del producto/servicio según el tipo.`,
    `• ${DOC_COL.qty}, ${DOC_COL.price}: obligatorios; precio unitario > 0.`,
    `• ${DOC_COL.unit}: solo para productos; código de unidad o vacío.`,
    `• ${DOC_COL.project}, ${DOC_COL.igv}, ${DOC_COL.desc}: opcionales (descripción solo servicios).`,
    '',
    'VALIDACIÓN AL IMPORTAR',
    '• Los ítems importados se añaden a los ya cargados (órdenes, guías, etc.).',
    '• El IGV del documento se aplica al calcular subtotales de filas importadas.',
    CONVERSION_IMPORT_NOTE,
    '• Máximo 2000 filas. Solo .xlsx.',
  ]);

  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(buf, 'plantilla-items-documento.xlsx');
}

/**
 * @returns {Promise<{ items: object[], errors: string[], warnings: string[] }>}
 */
export async function parseDocumentItemsExcel(file, {
  products,
  services,
  units,
  projects,
  igvRate = 18,
  productConversionsEnabled = false,
}) {
  const errors = [];
  const warnings = [];
  const items = [];
  const rate = parseFloat(igvRate) || 18;
  const loaded = await loadWorkbookSheet(file);
  if (loaded.error) {
    errors.push(loaded.error);
    return { items, errors, warnings };
  }
  const { sheet } = loaded;
  const found = findHeaderRow(sheet, [DOC_COL.sku, DOC_COL.qty, DOC_COL.price]);
  if (!found) {
    errors.push(
      `No se encontró la fila de encabezados. Debe incluir «${DOC_COL.sku}», «${DOC_COL.qty}» y «${DOC_COL.price}».`,
    );
    return { items, errors, warnings };
  }
  const { colMap, rowIndex: headerRow } = found;
  const cTipo = colMap[DOC_COL.tipo];
  const cSku = colMap[DOC_COL.sku];
  const cQty = colMap[DOC_COL.qty];
  const cUnit = colMap[DOC_COL.unit];
  const cPrice = colMap[DOC_COL.price];
  const cProj = colMap[DOC_COL.project];
  const cIgv = colMap[DOC_COL.igv];
  const cDesc = colMap[DOC_COL.desc];

  for (let r = headerRow + 1; r <= sheet.rowCount; r++) {
    const row = sheet.getRow(r);
    const skuVal = cSku ? cellText(row.getCell(cSku)) : '';
    const qtyVal = cQty ? cellText(row.getCell(cQty)) : '';
    if (!String(skuVal).trim() && !String(qtyVal).trim()) continue;
    if (items.length >= MAX_IMPORT_ROWS) {
      errors.push(`Solo se importan como máximo ${MAX_IMPORT_ROWS} filas.`);
      break;
    }
    if (!String(skuVal).trim()) {
      errors.push(`Fila ${r}: falta ${DOC_COL.sku}.`);
      continue;
    }

    const tipoRaw = cTipo ? cellText(row.getCell(cTipo)) : 'producto';
    const itemType = parseTipo(tipoRaw);
    if (!itemType) {
      errors.push(`Fila ${r}: tipo «${tipoRaw}» no reconocido. Use producto o servicio.`);
      continue;
    }

    const qty = parseQty(qtyVal, r, errors);
    if (qty == null) continue;
    const priceRaw = cPrice ? cellText(row.getCell(cPrice)) : '';
    const unitPrice = parsePrice(priceRaw, r, DOC_COL.price, errors);
    if (unitPrice == null) continue;

    const pRaw = cProj ? String(cellText(row.getCell(cProj)) || '').trim() : '';
    const projectId = resolveProject(projects, pRaw, r, errors);
    if (projectId === null) continue;

    const igvRaw = cIgv ? cellText(row.getCell(cIgv)) : '';
    const hasIgv = parseHasIgv(igvRaw);
    if (hasIgv === null) {
      errors.push(`Fila ${r}: ${DOC_COL.igv} no reconocido. Use si o no.`);
      continue;
    }

    const totals = buildDocumentTotals(qty, unitPrice, hasIgv, rate);
    const descExtra = cDesc ? String(cellText(row.getCell(cDesc)) || '').trim() : '';

    if (itemType === 'product') {
      const product = findProduct(products, skuVal);
      if (!product) {
        errors.push(`Fila ${r}: no se encontró producto «${skuVal}».`);
        continue;
      }
      const uRaw = cUnit ? String(cellText(row.getCell(cUnit)) || '').trim() : '';
      const uom = resolveUnit(units, uRaw, product, r, errors, productConversionsEnabled);
      if (uom === null) continue;

      items.push({
        item_type: 'product',
        product_id: product.id,
        service_id: '',
        unit_of_measure: uom.code,
        project_id: projectId,
        description: descExtra || product.name || '',
        quantity: qty,
        unit_price: unitPrice,
        has_igv: hasIgv,
        is_direct_consumption: false,
        ...totals,
        _label: product.name || skuVal,
        _tipoLabel: 'Producto',
        _unitFormula: uom.formula || '',
        _unitIsCustom: uom.isCustom,
      });
    } else {
      const service = findService(services, skuVal);
      if (!service) {
        errors.push(`Fila ${r}: no se encontró servicio «${skuVal}».`);
        continue;
      }
      if (cUnit && String(cellText(row.getCell(cUnit)) || '').trim()) {
        warnings.push(`Fila ${r}: la columna unidad se ignora en servicios.`);
      }
      items.push({
        item_type: 'service',
        product_id: '',
        service_id: service.id,
        project_id: projectId,
        description: descExtra || service.name || '',
        quantity: qty,
        unit_price: unitPrice,
        has_igv: hasIgv,
        ...totals,
        _label: service.name || skuVal,
        _tipoLabel: 'Servicio',
      });
    }
  }

  if (items.length === 0 && errors.length === 0) {
    errors.push('No se importó ninguna fila con datos.');
  }
  return { items, errors, warnings };
}

// ——— Requerimiento (solo productos y cantidades) ———

const REQ_COL = {
  sku: 'SKU o ID producto',
  qty: 'Cantidad',
  unit: 'Unidad (código)',
  project: 'Proyecto (código)',
  notes: 'Observaciones (opcional)',
};

export async function downloadRequirementItemsTemplate() {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Items', { views: [{ state: 'frozen', ySplit: 1 }] });
  const headers = [REQ_COL.sku, REQ_COL.qty, REQ_COL.unit, REQ_COL.project, REQ_COL.notes];
  const hRow = ws.addRow(headers);
  hRow.font = { bold: true };
  headers.forEach((_, i) => {
    hRow.getCell(i + 1).fill = HEADER_FILL;
  });
  ws.addRow(['EJ-SKU-001', 10, 'kg', 'PROJ-1', '']);
  ws.columns = headers.map((h) => ({ width: Math.min(28, Math.max(14, h.length + 2)) }));

  addInstructionsSheet(wb, [
    'LEYENDA Y USO DE LA PLANTILLA',
    'Requerimiento — ítems de productos (sin precios ni proveedor)',
    '',
    'COLUMNAS',
    `• ${REQ_COL.sku}: código SKU o ID numérico del producto (obligatorio).`,
    `• ${REQ_COL.qty}: cantidad mayor que 0 (obligatorio).`,
    `• ${REQ_COL.unit}: código de unidad; vacío = unidad del producto.`,
    `• ${REQ_COL.project}: código de proyecto; opcional.`,
    `• ${REQ_COL.notes}: texto libre por línea; opcional.`,
    '',
    'VALIDACIÓN AL IMPORTAR',
    '• Producto debe existir en el catálogo.',
    CONVERSION_IMPORT_NOTE,
    '• Máximo 2000 filas por archivo. Solo .xlsx.',
    '• Use la hoja «Items»; no cambie los nombres de la primera fila.',
  ]);

  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(buf, 'plantilla-items-requerimiento.xlsx');
}

export async function parseRequirementItemsExcel(file, { products, units, projects, productConversionsEnabled = false }) {
  const errors = [];
  const warnings = [];
  const items = [];
  const loaded = await loadWorkbookSheet(file);
  if (loaded.error) {
    errors.push(loaded.error);
    return { items, errors, warnings };
  }
  const { sheet } = loaded;
  const found = findHeaderRow(sheet, [REQ_COL.sku, REQ_COL.qty]);
  if (!found) {
    errors.push(
      `No se encontró la fila de encabezados. Debe incluir «${REQ_COL.sku}» y «${REQ_COL.qty}».`,
    );
    return { items, errors, warnings };
  }
  const { colMap, rowIndex: headerRow } = found;
  const cSku = colMap[REQ_COL.sku];
  const cQty = colMap[REQ_COL.qty];
  const cUnit = colMap[REQ_COL.unit];
  const cProj = colMap[REQ_COL.project];
  const cNotes = colMap[REQ_COL.notes];

  for (let r = headerRow + 1; r <= sheet.rowCount; r++) {
    const row = sheet.getRow(r);
    const skuVal = cSku ? cellText(row.getCell(cSku)) : '';
    const qtyVal = cQty ? cellText(row.getCell(cQty)) : '';
    if (!String(skuVal).trim() && !String(qtyVal).trim()) continue;
    if (items.length >= MAX_IMPORT_ROWS) {
      errors.push(`Solo se importan como máximo ${MAX_IMPORT_ROWS} filas.`);
      break;
    }
    if (!String(skuVal).trim()) {
      errors.push(`Fila ${r}: falta ${REQ_COL.sku}.`);
      continue;
    }
    const product = findProduct(products, skuVal);
    if (!product) {
      errors.push(`Fila ${r}: no se encontró producto «${skuVal}».`);
      continue;
    }
    const qty = parseQty(qtyVal, r, errors);
    if (qty == null) continue;

    const uRaw = cUnit ? String(cellText(row.getCell(cUnit)) || '').trim() : '';
    const uom = resolveUnit(units, uRaw, product, r, errors, productConversionsEnabled);
    if (uom === null) continue;

    const pRaw = cProj ? String(cellText(row.getCell(cProj)) || '').trim() : '';
    const projectId = resolveProject(projects, pRaw, r, errors);
    if (projectId === null) continue;

    const notes = cNotes ? String(cellText(row.getCell(cNotes)) || '').trim() : '';

    items.push({
      product_id: product.id,
      unit_of_measure: uom.code,
      project_id: projectId,
      quantity: qty,
      notes,
      _label: product.name || skuVal,
      _unitFormula: uom.formula || '',
      _unitIsCustom: uom.isCustom,
    });
  }

  if (items.length === 0 && errors.length === 0) {
    errors.push('No se importó ninguna fila con datos.');
  }
  return { items, errors, warnings };
}
